import React, { useEffect, useState } from 'react';
import { Activity, X, RotateCcw, Copy, Check, Terminal, EyeOff } from 'lucide-react';
import { useIsSystemAdmin } from '@/hooks/useIsSystemAdmin';
import { useDataUsageSnapshot } from '@/hooks/useDataUsageSnapshot';
import {
  dataUsageTracker,
  isDataUsageEnabled,
  isDataUsageConsoleLogEnabled,
  formatBytes,
  type UsageCategory,
} from '@/lib/dataUsageTracker';
import { debugLog } from '@/lib/videoPlayerDebug';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const OPEN_KEY = 'DATA_USAGE_MONITOR_OPEN';

/**
 * Panel display format: everything in whole MB. 0 → "0 MB", anything under a
 * full MB → "<1 MB", otherwise a rounded integer of MB. Kept local to the panel
 * so the shared formatBytes() (used by the console logger) is unchanged.
 */
function formatMB(bytes: number): string {
  if (bytes <= 0) return '0 MB';
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return '<1 MB';
  return `${Math.round(mb)} MB`;
}

const CATEGORY_LABEL: Record<UsageCategory, string> = {
  clip: 'Video playback',
  thumbnail: 'Thumbnails',
  full_video: 'Crop editor',
  music: 'Music',
  upload: 'Uploads ↑',
  control: 'API / overhead',
  app_asset: 'App & assets',
  other: 'Other',
};

// Display order for the breakdown — biggest/most-relevant first, plumbing last.
const CATEGORY_ORDER: UsageCategory[] = [
  'clip', 'thumbnail', 'full_video', 'music', 'upload', 'control', 'app_asset', 'other',
];

interface TipCopy {
  summary: string;   // plain-language, one short line
  detail: string;    // technical detail, one short line
}

const CATEGORY_TIP: Record<UsageCategory, TipCopy> = {
  clip: {
    summary: 'Video clips you played in the timeline.',
    detail: 'fMP4 segments fed into MSE. Exact bytes from arrayBuffer.byteLength — dominant cost.',
  },
  thumbnail: {
    summary: 'Small preview images in the timeline grid.',
    detail: 'Logical bytes (from stored file size), counted once per thumbnail across batch + refresh paths. Old uploads without a stored size show 0.',
  },
  full_video: {
    summary: 'Full-size videos loaded for the crop editor.',
    detail: 'context=editor downloads the whole file (no Range Requests for scrubbing).',
  },
  music: {
    summary: 'Background music tracks streaming during playback.',
    detail: 'Project or template music. Exact bytes from blob.size — re-activations (seek back) count as re-fetch waste.',
  },
  upload: {
    summary: 'Files you uploaded to the project.',
    detail: 'PUT to R2 via presigned URLs. Counted upstream from file.size — retries count as waste.',
  },
  control: {
    summary: 'Small behind-the-scenes requests (links, lists, permissions).',
    detail: 'Edge Function JSON: serve-media-batch, generate-upload-url, Supabase queries.',
  },
  app_asset: {
    summary: 'The app itself — code, styles, fonts. Loaded once per session.',
    detail: 'JS bundle / CSS / fonts. Same-origin so bytes are accurate; cached on repeat visits.',
  },
  other: {
    summary: 'Anything that didn’t match a known category.',
    detail: 'Caught by PerformanceObserver but uncategorised — usually nothing to worry about.',
  },
};

const WASTE_TIP: Record<'repair' | 'refetch' | 'prefetch' | 'clipsPlayedFetched', TipCopy> = {
  repair: {
    summary: 'A clip had to be rebuilt and downloaded a second time.',
    detail: 'Triggered when _cropped_mini.mp4 is missing: 404 → regenerate → re-fetch. Should be zero.',
  },
  refetch: {
    summary: 'The same file was downloaded more than once.',
    detail: 'Caused by backward seeks past the buffer, evicted segments, or upload retries.',
  },
  prefetch: {
    summary: 'Clips downloaded ahead of time that you never reached.',
    detail: 'MSE buffers ~30s ahead to prevent stalls. Some of this is necessary; excess is waste.',
  },
  clipsPlayedFetched: {
    summary: 'How many clips you watched out of how many we loaded.',
    detail: 'Played = became the current clip. Fetched = at least one byte downloaded.',
  },
};

const HEADLINE_TIP: TipCopy = {
  summary: 'Total data used in this session.',
  detail: 'The real number may be a bit higher — some cross-origin images don’t report their size.',
};

const RATE_TIP: TipCopy = {
  summary: 'Current download speed · time since session started · connection type.',
  detail: 'Speed is averaged over the last second. Connection comes from the Network Info API.',
};

// ---- Subcomponents (hoisted to module scope so they don't remount on every snapshot tick) ----

const TipContent: React.FC<{ tip: TipCopy }> = ({ tip }) => (
  <TooltipContent
    side="left"
    align="center"
    sideOffset={8}
    collisionPadding={8}
    className="max-w-[240px] bg-gray-900/95 backdrop-blur-md border-white/20 text-white text-[11px] leading-snug px-3 py-2 shadow-xl"
  >
    <div className="text-white">{tip.summary}</div>
    <div className="mt-1 text-white/55 text-[10px]">{tip.detail}</div>
  </TooltipContent>
);

const Row: React.FC<{ label: string; value: string; tip?: TipCopy; muted?: boolean }> = ({
  label, value, tip, muted,
}) => {
  const inner = (
    <div className={`flex items-center justify-between gap-3 py-0.5 rounded px-1 -mx-1 ${tip ? 'cursor-help hover:bg-white/5' : ''}`}>
      <span className={`text-[10px] ${muted ? 'text-white/40' : 'text-white/60'}`}>{label}</span>
      <span className="text-[10px] font-mono text-white/90">{value}</span>
    </div>
  );
  if (!tip) return inner;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{inner}</TooltipTrigger>
      <TipContent tip={tip} />
    </Tooltip>
  );
};

const HeadlineTip: React.FC<{ children: React.ReactNode; tip: TipCopy; side?: 'top' | 'bottom' | 'left' | 'right' }> = ({
  children, tip, side = 'bottom',
}) => (
  <Tooltip>
    <TooltipTrigger asChild><span className="cursor-help">{children}</span></TooltipTrigger>
    <TooltipContent
      side={side}
      sideOffset={6}
      collisionPadding={8}
      className="max-w-[240px] bg-gray-900/95 backdrop-blur-md border-white/20 text-white text-[11px] leading-snug px-3 py-2 shadow-xl"
    >
      <div className="text-white">{tip.summary}</div>
      <div className="mt-1 text-white/55 text-[10px]">{tip.detail}</div>
    </TooltipContent>
  </Tooltip>
);

/**
 * Admin-only floating panel showing near-real-time session data usage.
 *
 * Renders only for verified system admins when monitoring is enabled (local dev
 * or DEBUG_DATA_USAGE). Toggle with Ctrl/Cmd+Shift+D. Reads the in-memory
 * tracker — no network, no DB.
 */
export const DataUsageMonitor: React.FC = () => {
  const isAdmin = useIsSystemAdmin();
  const [open, setOpen] = useState(() => localStorage.getItem(OPEN_KEY) === 'true');
  const [copied, setCopied] = useState(false);
  // Both gates re-read together — the settings switch flips both flags as a
  // single master on/off, and we want the chip to mount/unmount immediately.
  const [enabled, setEnabled] = useState(() => isDataUsageEnabled());
  const [showDebugUi, setShowDebugUi] = useState(() => isDataUsageConsoleLogEnabled());

  useEffect(() => {
    const sync = () => {
      setEnabled(isDataUsageEnabled());
      setShowDebugUi(isDataUsageConsoleLogEnabled());
    };
    window.addEventListener('storage', sync);
    window.addEventListener('data-usage-debug-changed', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('data-usage-debug-changed', sync);
    };
  }, []);

  const visible = isAdmin && enabled && showDebugUi;
  // Poll whenever the panel is visible (open OR collapsed pill) so the
  // launcher's headline byte count updates live without a click.
  const { snapshot, rateBytesPerSec } = useDataUsageSnapshot(visible);

  // Ctrl/Cmd+Shift+D toggles the panel (admins only).
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'D' || e.key === 'd')) {
        e.preventDefault();
        setOpen(prev => {
          const next = !prev;
          localStorage.setItem(OPEN_KEY, String(next));
          debugLog.userAction(`Data monitor ${next ? 'opened' : 'closed'} (Ctrl/Cmd+Shift+D)`);
          return next;
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible]);

  if (!visible) return null;

  const setOpenPersist = (next: boolean) => {
    setOpen(next);
    localStorage.setItem(OPEN_KEY, String(next));
  };

  if (!open) {
    return (
      <button
        onClick={() => { setOpenPersist(true); debugLog.userAction('Data monitor opened (launcher)'); }}
        title="Data usage monitor (Ctrl/Cmd+Shift+D)"
        className="fixed bottom-16 right-3 md:bottom-3 md:right-3 z-[9999] flex items-center gap-1.5 px-2.5 py-1.5 rounded-full
          bg-gray-900/90 backdrop-blur-md border border-white/20 text-white/80 hover:text-white
          hover:bg-gray-900 shadow-lg text-[11px] font-medium"
      >
        <Activity className="w-3.5 h-3.5 text-indigo-300" />
        {formatMB(snapshot.totalWireBytes)}
      </button>
    );
  }

  // Show all categories in a fixed order — including 0 B ones — so the full
  // picture is visible (e.g. confirm Uploads/Crop editor appear once used).
  const categories = CATEGORY_ORDER;

  const conn = snapshot.connection
    ? `${snapshot.connection.effectiveType ?? '?'}${snapshot.connection.saveData ? ' · saveData' : ''}`
    : 'n/a';

  // Fully turn the monitor off for the session — mirrors the "Show Data Monitor"
  // toggle in Edit Profile → Developer Tools. Removes the panel and the chip
  // (re-enable from that toggle). The X button only collapses to the pill.
  const hideMonitor = () => {
    localStorage.removeItem('DEBUG_DATA_USAGE_CONSOLE');
    window.dispatchEvent(new Event('data-usage-debug-changed'));
    debugLog.userAction('Data monitor — hidden for session');
  };

  const copyJson = async () => {
    await navigator.clipboard.writeText(JSON.stringify(snapshot, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    debugLog.userAction('Data monitor — copied snapshot JSON');
  };

  return (
    <TooltipProvider delayDuration={150} skipDelayDuration={300} disableHoverableContent={false}>
      <div className="fixed bottom-16 right-3 md:bottom-3 md:right-3 z-[9999] w-72 max-w-[calc(100vw-1.5rem)] bg-gray-900/95 backdrop-blur-md border border-white/20
        rounded-lg shadow-xl text-white">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
          <div className="flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5 text-indigo-300" />
            <span className="text-[11px] font-semibold">Data Usage</span>
          </div>
          <button
            onClick={() => { setOpenPersist(false); debugLog.userAction('Data monitor closed'); }}
            className="p-0.5 hover:bg-white/10 rounded" title="Close (Ctrl/Cmd+Shift+D)"
          >
            <X className="w-3.5 h-3.5 text-white/50 hover:text-white/90" />
          </button>
        </div>

        <div className="p-3 space-y-2">
          {/* Headline */}
          <div className="flex items-baseline justify-between">
            <HeadlineTip tip={HEADLINE_TIP}>
              <span className="text-lg font-semibold font-mono">{formatMB(snapshot.totalWireBytes)}</span>
            </HeadlineTip>
            <HeadlineTip tip={RATE_TIP}>
              <span className="text-[10px] text-white/50">
                {formatBytes(rateBytesPerSec)}/s · {(snapshot.elapsedMs / 1000).toFixed(0)}s · {conn}
              </span>
            </HeadlineTip>
          </div>

          {/* Per-activity breakdown — all categories, 0 B ones muted */}
          <div className="border-t border-white/10 pt-1.5">
            {categories.map(c => {
              const s = snapshot.byCategory[c];
              return (
                <Row
                  key={c}
                  label={`${CATEGORY_LABEL[c]} (${s.requests})`}
                  value={formatMB(s.wireBytes)}
                  tip={CATEGORY_TIP[c]}
                  muted={s.requests === 0}
                />
              );
            })}
          </div>

          {/* Waste */}
          <div className="border-t border-white/10 pt-1.5">
            <div className="text-[9px] uppercase tracking-wide text-white/40 mb-0.5">Waste</div>
            <Row label="Repair re-downloads" value={formatMB(snapshot.waste.repairBytes)} muted tip={WASTE_TIP.repair} />
            <Row label={`Re-fetch (${snapshot.waste.duplicateUrlCount} dupes)`} value={formatMB(snapshot.waste.reFetchBytes)} muted tip={WASTE_TIP.refetch} />
            <Row label="Prefetched, unwatched" value={formatMB(snapshot.prefetchWasteBytes)} muted tip={WASTE_TIP.prefetch} />
            <Row label="Clips played / fetched" value={`${snapshot.clipsPlayed} / ${snapshot.clipsFetched}`} muted tip={WASTE_TIP.clipsPlayedFetched} />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 border-t border-white/10 pt-2">
            <button
              onClick={() => { dataUsageTracker.reset(); debugLog.userAction('Data monitor — reset session'); }}
              className="flex items-center gap-1 px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-[10px]"
              title="Reset session counters"
            >
              <RotateCcw className="w-3 h-3" /> Reset
            </button>
            <button
              onClick={copyJson}
              className="flex items-center gap-1 px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-[10px]"
              title="Copy snapshot as JSON"
            >
              {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />} Copy
            </button>
            <button
              onClick={() => { dataUsageTracker.logSummary('manual', { force: true }); debugLog.userAction('Data monitor — logged to console'); }}
              className="flex items-center gap-1 px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-[10px]"
              title="Log summary to console"
            >
              <Terminal className="w-3 h-3" /> Log
            </button>
            <button
              onClick={hideMonitor}
              className="ml-auto flex items-center gap-1 px-2 py-1 rounded bg-white/10 hover:bg-white/20 text-[10px]"
              title="Hide the monitor for this session (re-enable in Edit Profile → Developer Tools)"
            >
              <EyeOff className="w-3 h-3" /> Hide
            </button>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};
